<template>
  <div>
    <form @submit.prevent="submit">
      <div class="pl-2 pr-4 py-4">
        <div class="columns is-mobile is-variable is-1 is-vcentered">
          <div class="column is-narrow">
            <b-tooltip label="Kembali" type="is-dark">
              <button
                type="button"
                class="button is-white"
                @click="$router.go(-1)"
              >
                <b-icon icon="arrow-left"></b-icon>
              </button>
            </b-tooltip>
          </div>
          <div class="column">
            <h1 class="title is-5 has-text-weight-medium">
              {{ onEdit ? "Ubah" : "Tambah" }}
            </h1>
          </div>
          <div class="column is-narrow has-text-right">
            <b-button
              native-type="submit"
              type="is-primary"
              icon-left="check-bold"
            >
              Simpan
            </b-button>
          </div>
        </div>
      </div>

      <div class="p-4" style="max-width: 500px">
        <b-notification
          type="is-danger"
          aria-close-label="Tutup"
          role="alert"
          v-if="error"
          :closable="false"
        >
          {{ error | capitalize }}
        </b-notification>

        <b-field label="Email *">
          <b-input type="email" v-model="email" required></b-input>
        </b-field>

        <b-field label="Nama Lengkap *">
          <b-input v-model="name" required></b-input>
        </b-field>

        <b-field label="Gelar Depan">
          <b-input v-model="frontDegree"></b-input>
        </b-field>

        <b-field label="Gelar Belakang">
          <b-input v-model="backDegree"></b-input>
        </b-field>

        <hr />

        <b-message type="is-warning" v-if="!onEdit">
          NIP akan digunakan sebagai
          <span class="has-text-weight-medium">kata sandi</span> awal pengguna.
        </b-message>

        <b-field label="NIP (Nomor Induk Kepegawaian) *">
          <b-input v-model="nip" minlength="18" required></b-input>
        </b-field>

        <b-field label="Unit Kerja *" grouped>
          <b-autocomplete
            v-model="employeeUnitName"
            :data="employeeUnitsData"
            placeholder="Ketik untuk mencari.."
            field="name"
            :loading="isLoadingEmployeeUnit"
            @typing="onSearchEmployeeUnits"
            @select="selectEmployeeUnit"
            :open-on-focus="true"
            :keep-first="true"
            icon-right="chevron-down"
            expanded
            required
          >
            <template slot-scope="props">
              {{ props.option.name | lowercase | capitalize }}
            </template>
            <template slot="empty">
              Tidak ada hasil untuk {{ employeeUnitName | truncate(20) }}
            </template>
            <template slot="footer"
              >{{ employeeUnitsHasMore ? "..." : "" }}
            </template>
          </b-autocomplete>

          <p class="control">
            <b-button
              icon-pack="mdi"
              icon-left="plus"
              type="is-primary"
              @click.prevent="addEmployeeUnitDialog()"
            >
              Baru
            </b-button>
          </p>
        </b-field>

        <b-field label="Shift Kerja *" grouped>
          <b-select
            placeholder="Pilih shift"
            required
            v-model="shiftId"
            :loading="isLoadingUnitShifts"
            expanded
          >
            <option
              v-for="(option, index) in shiftsData"
              :value="option.id"
              :key="option.id"
            >
              Shift {{ index + 1 }}
            </option>
          </b-select>
        </b-field>

        <hr />

        <b-field label="Foto Profil (URL)" grouped>
          <b-input v-model="photoUrl" disabled expanded></b-input>

          <p class="control" v-if="photoUrl">
            <b-button
              tag="a"
              :href="photoUrl"
              target="_blank"
              icon-pack="mdi"
              icon-right="chevron-right"
              type="is-primary"
            >
              Lihat
            </b-button>
          </p>
        </b-field>

        <b-field label="Jenis Kelamin *" expanded>
          <b-field>
            <b-radio-button
              v-model="gender"
              name="gender"
              native-value="m"
              type="is-info"
              required
            >
              <b-icon icon="gender-male"></b-icon>
              Laki-laki
            </b-radio-button>

            <b-radio-button
              v-model="gender"
              name="gender"
              type="is-danger"
              native-value="f"
              required
            >
              <b-icon icon="gender-female"></b-icon>
              Perempuan
            </b-radio-button>
          </b-field>
        </b-field>

        <b-field label="Tanggal Lahir">
          <b-input v-model="birthDateString" expanded></b-input>
          <b-datepicker
            ref="datepicker"
            v-model="birthDate"
            :open-on-focus="false"
            :mobile-native="false"
          >
            <template slot="trigger">
              <b-button
                @click="$refs.datepicker.toggle()"
                icon-left="calendar-today"
                type="is-primary"
                >Pilih
              </b-button>
            </template>
          </b-datepicker>
        </b-field>

        <b-field label="Kota Lahir">
          <b-input v-model="birthPlace"></b-input>
        </b-field>

        <b-field label="Alamat Tinggal">
          <b-input v-model="address" maxlength="200" type="textarea"></b-input>
        </b-field>

        <small
          ><i><span class="mr-2">*</span> : Wajib diisi</i></small
        >
      </div>
    </form>
  </div>
</template>

<script>
import debounce from "lodash/debounce";
import { DateTime } from "luxon";

export default {
  name: "EmployeeForm",
  data() {
    return {
      error: "",
      id: this.$route.params.id,
      employeeUnitName: "",
      employeeUnitsData: [],
      employeeUnitsHasMore: "",
      shiftsData: [],
      shiftId: undefined,
      isLoadingEmployeeUnit: false,
      isLoadingUnitShifts: false,
      nip: "",
      name: "",
      email: "",
      photoUrl: "",
      employeeUnitId: "",
      frontDegree: "",
      backDegree: "",
      birthDate: undefined,
      birthPlace: "",
      gender: "",
      address: ""
    };
  },
  methods: {
    onSearchEmployeeUnits: debounce(function(name) {
      this.employeeUnitName = name;
      this.loadEmployeeUnits();
    }, 500),
    loadEmployee() {
      const loadingComponent = this.$buefy.loading.open();

      this.$http({
        url: `employee_profiles/${this.id}`,
        method: "GET"
      })
        .then(res => {
          const data = res.data.data;
          const birthDate = data.birth_date
            ? new Date(data.birth_date)
            : undefined;

          this.nip = data.nip;
          this.name = data.name;
          this.email = data.email;
          this.employeeUnitName = data.unit?.name;
          this.employeeUnitId = data.employee_unit_id;
          this.shiftId = data.employee_unit_shift_id;
          this.frontDegree = data.front_degree;
          this.backDegree = data.back_degree;
          this.birthDate = birthDate;
          this.birthPlace = data.birth_place;
          this.gender = data.gender;
          this.address = data.address;
          this.photoUrl = data.image_url;

          this.loadUnitShifts();

          loadingComponent.close();
        })
        .catch(() => {
          loadingComponent.close();

          this.$buefy.snackbar.open({
            message: "Gagal memuat daftar Pegawai",
            type: "is-warning",
            duration: 5000,
            actionText: "Coba Lagi",
            onAction: () => {
              this.loadEmployeeUnits();
            }
          });
        });
    },
    loadEmployeeUnits() {
      this.isLoadingEmployeeUnit = true;

      this.$http({
        url: `employee_units`,
        method: "GET",
        params: {
          search: this.employeeUnitName,
          sort: "name",
          direction: "asc"
        }
      })
        .then(res => {
          this.employeeUnitsData = res.data.data;
          this.employeeUnitsHasMore = res.data.meta.has_more;
          this.isLoadingEmployeeUnit = false;
        })
        .catch(() => {
          this.isLoadingEmployeeUnit = false;

          this.$buefy.snackbar.open({
            message: "Gagal memuat daftar Unit kerja",
            type: "is-warning",
            duration: 5000,
            actionText: "Coba Lagi",
            onAction: () => {
              this.loadEmployeeUnits();
            }
          });
        });
    },
    loadUnitShifts() {
      this.isLoadingUnitShifts = true;

      this.$http({
        url: `employee_units/${this.employeeUnitId}/shifts`,
        method: "GET"
      })
        .then(res => {
          this.shiftsData = res.data.data;
          this.isLoadingUnitShifts = false;
        })
        .catch(() => {
          this.isLoadingUnitShifts = false;

          this.$buefy.snackbar.open({
            message: "Gagal memuat daftar Shift Unit",
            type: "is-warning",
            duration: 5000,
            actionText: "Coba Lagi",
            onAction: () => {
              this.loadUnitShifts();
            }
          });
        });
    },
    submit() {
      this.error = "";
      const loadingComponent = this.$buefy.loading.open();

      const mode = this.getRequestProps({
        id: this.id,
        onEdit: this.onEdit,
        basePath: "employee_profiles",
        modelName: "Pegawai"
      });

      this.$http({
        url: mode.url,
        method: mode.method,
        data: {
          nip: this.nip,
          name: this.name,
          email: this.email,
          employee_unit_id: this.employeeUnitId,
          employee_unit_shift_id: this.shiftId,
          front_degree: this.frontDegree,
          back_degree: this.backDegree,
          birth_date: this.birthDateStringIso,
          birth_place: this.birthPlace,
          gender: this.gender,
          address: this.address
        }
      })
        .then(() => {
          this.$buefy.toast.open({
            message: `Berhasil ${mode.message} <i class="mdi mdi-check-bold has-text-success"></i>`
          });

          this.$router.push({ name: "employee" });
          loadingComponent.close();
        })
        .catch(error => {
          loadingComponent.close();

          if (error.response.status === 400) {
            const errors = error.response.data.errors;
            this.error = errors[Object.keys(errors)[0]][0];
            return;
          }

          this.$buefy.snackbar.open({
            message: `Gagal ${mode.message}`,
            type: "is-warning",
            duration: 5000,
            actionText: "Coba Lagi",
            onAction: () => {
              this.submit();
            }
          });
        });
    },
    selectEmployeeUnit(option) {
      if (option) {
        this.employeeUnitId = option.id;
      }
    },
    addEmployeeUnitDialog(value) {
      this.$buefy.dialog.prompt({
        message: `Nama Unit Kerja`,
        confirmText: "Simpan",
        cancelText: "Batal",
        inputAttrs: {
          value: value,
          maxlength: 30
        },
        trapFocus: true,
        onConfirm: this.addEmployeeUnit
      });
    },
    addEmployeeUnit(name) {
      this.isLoadingEmployeeUnit = true;

      const formData = new FormData();
      formData.append("name", this.$options.filters.capitalize(name));

      this.$http({
        url: `employee_units`,
        method: "POST",
        data: formData
      })
        .then(res => {
          this.isLoadingEmployeeUnit = false;

          const employeeUnit = res.data.data;
          this.employeeUnitsData.push(employeeUnit);
          this.employeeUnitId = employeeUnit.id;
          this.employeeUnitName = employeeUnit.name;

          this.$buefy.toast.open({
            message:
              'Berhasil menambahkan Unit Kerja baru <i class="mdi mdi-check-bold has-text-success"></i>'
          });
        })
        .catch(() => {
          this.isLoadingEmployeeUnit = false;

          this.$buefy.snackbar.open({
            message: "Gagal menambakan Unit Kerja baru",
            type: "is-warning",
            duration: 5000,
            actionText: "Coba Lagi",
            onAction: () => this.addEmployeeUnit(name)
          });
        });
    }
  },
  computed: {
    birthDateString: {
      get: function() {
        return this.birthDate?.toLocaleString("id-ID", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit"
        });
      },
      set(value) {
        const date = DateTime.fromFormat(value, "dd/LL/yyyy");
        if (date.isValid) {
          this.birthDate = new Date(date.toISODate());
        }
      }
    },
    birthDateStringIso() {
      if (!this.birthDateString) {
        return "";
      }

      const date = DateTime.fromFormat(this.birthDateString, "dd/LL/yyyy");
      return date.toISODate();
    },
    onEdit() {
      return this.id;
    }
  },
  created() {
    this.loadEmployeeUnits();

    if (this.onEdit) this.loadEmployee();
  }
};
</script>
